/**
 * Move the moov atom before mdat so mobile browsers can read metadata without
 * buffering the entire file (qt-faststart / ffmpeg -movflags +faststart).
 */

type Mp4Box = {
  type: string;
  start: number;
  size: number;
  headerSize: number;
};

function readU32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset]! << 24) |
      (bytes[offset + 1]! << 16) |
      (bytes[offset + 2]! << 8) |
      bytes[offset + 3]!) >>>
    0
  );
}

function writeU32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
}

function readBox(bytes: Uint8Array, offset: number): Mp4Box | null {
  if (offset + 8 > bytes.length) return null;
  let size = readU32(bytes, offset);
  const type = String.fromCharCode(
    bytes[offset + 4]!,
    bytes[offset + 5]!,
    bytes[offset + 6]!,
    bytes[offset + 7]!
  );
  let headerSize = 8;
  if (size === 1) {
    if (offset + 16 > bytes.length) return null;
    const hi = readU32(bytes, offset + 8);
    const lo = readU32(bytes, offset + 12);
    size = hi * 0x1_0000_0000 + lo;
    headerSize = 16;
  } else if (size === 0) {
    size = bytes.length - offset;
  }
  if (size < headerSize) return null;
  return { type, start: offset, size, headerSize };
}

function listTopLevelBoxes(bytes: Uint8Array): Mp4Box[] {
  const out: Mp4Box[] = [];
  let offset = 0;
  while (offset + 8 <= bytes.length) {
    const box = readBox(bytes, offset);
    if (!box) break;
    out.push(box);
    if (box.size <= 0) break;
    offset += box.size;
    if (offset > bytes.length) break;
  }
  return out;
}

function walkBoxes(
  bytes: Uint8Array,
  start: number,
  end: number,
  containerTypes: Set<string>,
  visit: (box: Mp4Box) => void
): void {
  let offset = start;
  while (offset + 8 <= end) {
    const box = readBox(bytes, offset);
    if (!box || box.start + box.size > end) break;
    visit(box);
    if (containerTypes.has(box.type)) {
      walkBoxes(bytes, box.start + box.headerSize, box.start + box.size, containerTypes, visit);
    }
    offset += box.size;
  }
}

function patchChunkOffsets(moov: Uint8Array, delta: number): void {
  const containers = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl']);
  walkBoxes(moov, 0, moov.length, containers, box => {
    if (box.type === 'stco') {
      const base = box.start + box.headerSize;
      if (base + 8 > moov.length) return;
      const count = readU32(moov, base + 4);
      for (let i = 0; i < count; i++) {
        const off = base + 8 + i * 4;
        if (off + 4 > moov.length) break;
        writeU32(moov, off, readU32(moov, off) + delta);
      }
    } else if (box.type === 'co64') {
      const base = box.start + box.headerSize;
      if (base + 8 > moov.length) return;
      const count = readU32(moov, base + 4);
      for (let i = 0; i < count; i++) {
        const off = base + 8 + i * 8;
        if (off + 8 > moov.length) break;
        const hi = readU32(moov, off);
        const lo = readU32(moov, off + 4);
        const value = (BigInt(hi) << 32n) | BigInt(lo);
        const next = value + BigInt(delta);
        writeU32(moov, off, Number(next >> 32n));
        writeU32(moov, off + 4, Number(next & 0xffff_ffffn));
      }
    }
  });
}

/** Returns remuxed bytes when moov was after mdat; otherwise null. */
export function mp4FastStartBytes(bytes: Uint8Array): Uint8Array | null {
  if (bytes.length < 16) return null;
  const top = listTopLevelBoxes(bytes);
  const mdat = top.find(b => b.type === 'mdat');
  const moov = top.find(b => b.type === 'moov');
  if (!mdat || !moov || moov.start <= mdat.start) return null;

  const moovCopy = bytes.slice(moov.start, moov.start + moov.size);
  patchChunkOffsets(moovCopy, moov.size);

  const beforeMdat = bytes.slice(0, mdat.start);
  const mdatAndRest = bytes.slice(mdat.start);
  const out = new Uint8Array(beforeMdat.length + moovCopy.length + mdatAndRest.length);
  out.set(beforeMdat, 0);
  out.set(moovCopy, beforeMdat.length);
  out.set(mdatAndRest, beforeMdat.length + moovCopy.length);
  return out;
}
